import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { forkJoin } from 'rxjs';
import { Category } from '../../community/shared/models/category.model';
import { ActionType, CommunityItem, CommunityItemStatus, CommunityItemType } from '../../community/shared/models/community-item.model';
import { CommunityService } from '../services/community.service';
import {
    CommunityItemInput,
    getCategoryIdForType,
    getDefaultCategories,
    mapCommunitiesResponse,
    mapCommunityResponse,
    toCommunityItemInput,
    toCommunityRequestBody
} from '../utils/community.mapper';

export interface CommunityState {
    items: CommunityItem[];
    categories: Category[];
    loading: boolean;
    saving: boolean;
    error: string | null;
    successMessage: string | null;
}

export const COMMUNITY_PREVIEW_DEFAULTS = {
    dashboardItemCount: 2,
    itemsPerRowDesktop: 2,
    showOnlyActiveItems: true
} as const;

const TYPE_TO_CATEGORY: Record<CommunityItemType, string> = {
    webinar: 'cat-webinar',
    resource: 'cat-resource',
    project: 'cat-project',
    event: 'cat-event',
    video: 'cat-video',
    external_link: 'cat-external'
};

const initialState: CommunityState = {
    items: [],
    categories: getDefaultCategories(),
    loading: false,
    saving: false,
    error: null,
    successMessage: null
};

function buildItemInput(
    item: Omit<CommunityItem, 'id' | 'updatedAt' | 'updatedBy' | 'categoryId' | 'imageUrl' | 'visible'> & {
        order: number;
    }
): CommunityItemInput {
    return {
        type: item.type,
        title: item.title,
        description: item.description,
        buttonText: item.buttonText,
        actionType: item.actionType,
        destination: item.destination,
        status: item.status === 'scheduled' ? 'draft' : item.status,
        order: item.order
    };
}

export const CommunityStore = signalStore(
    withState(initialState),
    withComputed((store) => ({
        categoryMap: computed(() => new Map(store.categories().map((category) => [category.id, category]))),
        sortedItems: computed(() => [...store.items()].sort((a, b) => a.order - b.order))
    })),
    withMethods((store, api = inject(CommunityService)) => ({
        clearSuccessMessage() {
            patchState(store, { successMessage: null });
        },

        getCategoryById(id: string): Category | undefined {
            return store.categoryMap().get(id);
        },

        getCategoryForType(type: CommunityItemType): Category | undefined {
            const categoryId = TYPE_TO_CATEGORY[type];
            return categoryId ? store.categoryMap().get(categoryId) : undefined;
        },

        loadItems(onComplete?: () => void) {
            patchState(store, { loading: true, error: null, successMessage: null });

            api.getCommunities().subscribe({
                next: (response) => {
                    patchState(store, {
                        items: mapCommunitiesResponse(response),
                        loading: false
                    });
                    onComplete?.();
                },
                error: () => {
                    patchState(store, {
                        loading: false,
                        error: 'Unable to load community items.'
                    });
                    onComplete?.();
                }
            });
        },

        createItem(
            item: Omit<CommunityItem, 'id' | 'updatedAt' | 'updatedBy' | 'categoryId' | 'imageUrl' | 'visible'> & {
                order: number;
            },
            onComplete?: (success: boolean) => void
        ) {
            const payload = toCommunityRequestBody(buildItemInput(item));

            patchState(store, { saving: true, error: null, successMessage: null });

            api.createCommunity(payload).subscribe({
                next: (response) => {
                    const created = mapCommunityResponse(response);
                    patchState(store, {
                        saving: false,
                        successMessage: 'Community item created successfully.',
                        ...(created
                            ? { items: [...store.items(), created] }
                            : {})
                    });

                    if (!created) {
                        this.loadItems();
                    }

                    onComplete?.(true);
                },
                error: () => {
                    patchState(store, {
                        saving: false,
                        error: 'Unable to create community item.'
                    });
                    onComplete?.(false);
                }
            });
        },

        updateItem(id: string, changes: Partial<CommunityItem>, onComplete?: (success: boolean) => void) {
            const existing = store.items().find((item) => item.id === id);
            if (!existing) {
                onComplete?.(false);
                return;
            }

            const merged: CommunityItem = {
                ...existing,
                ...changes,
                categoryId: changes.type
                    ? getCategoryIdForType(changes.type)
                    : existing.categoryId
            };
            const payload = toCommunityRequestBody(toCommunityItemInput(merged), id);

            patchState(store, { saving: true, error: null, successMessage: null });

            api.updateCommunity(id, payload).subscribe({
                next: (response) => {
                    const updated = mapCommunityResponse(response);

                    patchState(store, {
                        saving: false,
                        successMessage: 'Community item updated successfully.',
                        items: updated
                            ? store.items().map((item) => (item.id === id ? updated : item))
                            : store.items().map((item) =>
                                  item.id === id
                                      ? {
                                            ...merged,
                                            visible: merged.status === 'published',
                                            updatedAt: new Date().toISOString()
                                        }
                                      : item
                              )
                    });

                    onComplete?.(true);
                },
                error: () => {
                    patchState(store, {
                        saving: false,
                        error: 'Unable to update community item.'
                    });
                    onComplete?.(false);
                }
            });
        },

        deleteItem(id: string) {
            patchState(store, { saving: true, error: null, successMessage: null });

            api.deleteCommunity(id).subscribe({
                next: () => {
                    patchState(store, {
                        saving: false,
                        successMessage: 'Community item deleted successfully.',
                        items: store.items().filter((item) => item.id !== id)
                    });
                },
                error: () => {
                    patchState(store, {
                        saving: false,
                        error: 'Unable to delete community item.'
                    });
                }
            });
        },

        reorderItems(
            newOrder: string[],
            onComplete?: (success: boolean) => void,
            movedItemIds: string[] = []
        ) {
            const itemMap = new Map(store.items().map((item) => [item.id, item]));
            const movedIdSet = new Set(movedItemIds);
            const changedUpdates = newOrder.flatMap((id, index) => {
                const item = itemMap.get(id);
                if (!item) {
                    return [];
                }

                const newOrderValue = index + 1;
                const orderChanged = item.order !== newOrderValue;
                const shouldUpdate = movedIdSet.size > 0 ? movedIdSet.has(id) && orderChanged : orderChanged;

                if (!shouldUpdate) {
                    return [];
                }

                return [
                    {
                        id,
                        newOrderValue,
                        request$: api.updateCommunity(
                            id,
                            toCommunityRequestBody(
                                toCommunityItemInput({
                                    ...item,
                                    order: newOrderValue
                                }),
                                id
                            )
                        )
                    }
                ];
            });

            if (!changedUpdates.length) {
                onComplete?.(true);
                return;
            }

            patchState(store, { saving: true, error: null, successMessage: null });

            forkJoin(changedUpdates.map((update) => update.request$)).subscribe({
                next: () => {
                    const reordered = newOrder
                        .map((id, index) => {
                            const item = itemMap.get(id);
                            return item ? { ...item, order: index + 1 } : null;
                        })
                        .filter((item): item is CommunityItem => item !== null);

                    const untouched = store.items().filter((item) => !newOrder.includes(item.id));

                    patchState(store, {
                        saving: false,
                        successMessage: 'Display order saved successfully.',
                        items: [...reordered, ...untouched]
                    });
                    onComplete?.(true);
                },
                error: () => {
                    patchState(store, {
                        saving: false,
                        error: 'Unable to save display order.'
                    });
                    onComplete?.(false);
                }
            });
        }
    }))
);

export const ACTIVE_COMMUNITY_ITEM_TYPES = [
    'webinar',
    'project',
    'event',
    'external_link'
    // 'resource',
    // 'video'
] as const satisfies readonly CommunityItemType[];

export const COMMUNITY_ITEM_TYPE_LABELS: Record<CommunityItemType, string> = {
    webinar: 'Webinar',
    resource: 'Resource',
    project: 'Project',
    event: 'Event',
    video: 'Video',
    external_link: 'External Link'
};

export const COMMUNITY_ITEM_TYPES: { value: CommunityItemType | 'all'; label: string }[] = [
    { value: 'all', label: 'All Types' },
    ...ACTIVE_COMMUNITY_ITEM_TYPES.map((type) => ({
        value: type,
        label: COMMUNITY_ITEM_TYPE_LABELS[type]
    }))
];

export const COMMUNITY_ITEM_STATUSES: { value: CommunityItemStatus | 'all'; label: string }[] = [
    { value: 'all', label: 'All Status' },
    { value: 'published', label: 'Published' },
    { value: 'draft', label: 'Draft' }
];

export const ACTION_TYPES: { value: ActionType; label: string }[] = [
    { value: 'internal', label: 'Internal' },
    { value: 'external', label: 'External' }
];
